import * as React from 'react';
import { flow, forEach, map } from 'lodash';
import {IBrandingState, II18nMessages } from '../../../../services/branding/models';
import { Col, ControlLabel, Form, FormGroup, Row } from 'react-bootstrap';
import { Field, formValueSelector, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { IChannel, ICustomer, IPremium, IProduct } from '../../../../data/api/models';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import FieldFormControl from '../../../../components/FieldFormControl';
import NavigationBar from '../../components/NavigationBar';
import {isLuhnHappy, isRequired, isLengthBetween} from '../../../../util/validators';
import Cards from 'react-credit-cards';
import FaExclamationTriangle from 'react-icons/fa/exclamation-triangle';
import DeclinesAndReferrals from '../../components/DeclinesAndReferrals';
import SaveQuoteModal from '../../components/SaveQuoteModal';
import {getAvailableQuote, PRODUCT_NAME, translatePaymentError, displayCurrency} from '../../../../util/helpers';
import * as moment from 'moment';

interface IPaymentFormProps extends InjectedFormProps {
  cvv: string;
  dispatch: any;
  password: string;
  cardName: string;
  product: IProduct;
  renewal: boolean;
  channel: IChannel;
  premium: IPremium;
  quote: IQuoteState;
  onBack: () => void;
  cardNumber: string;
  expiryYear: string;
  customer: ICustomer;
  i18n: II18nMessages;
  paymentType: string;
  expiryMonth: string;
  errorMessage: string;
  branding: IBrandingState;
  encryptCardNumber: string;
  policyDeclaration: boolean;
  passwordConfirmation: string;
  purchaseDeclaration: boolean;
  renderDefinition(name: string): any;
  handleCalculate: any;
  actions: {
    submit(name: string);
    push: (route: string) => void;
    quote: IQuoteActions;
  };
}

interface IPaymentFormState {
  activeField?: string;
  gross: number;
  referrals: any[];
  declines: any[];
  errors: string[];
}

class PaymentForm extends React.Component<IPaymentFormProps, IPaymentFormState> {
  public state = {
    activeField: null,
    gross: 0,
    declines: [],
    referrals: [],
    errors: [],
  };

  public componentDidMount() {
    const { quote, dispatch, change, actions, renewal } = this.props;

    // restore bi-annual selection after save
    if (quote.application.paymentSchedule === 'bi-annual') {
      dispatch(change('recurringCard', true));
      dispatch(change('paymentType', 'recurring card'));
      if (!quote.biannualFirstAmount) {
        actions.quote.getBiAnnualFirstAmount(quote.application.id);
      }
    }

    // set payment date in the future on any policy starting 01/01
    if (renewal || moment(quote.application.startDate).dayOfYear() === 1) {
      dispatch(change('paymentDate', moment(quote.application.startDate, 'DD/MM/YYYY').add(1, 'day').format()));
    }
  }

  public componentWillReceiveProps(nextProps) {
    const { quote } = this.props;

    const availableQuote = getAvailableQuote(quote);
    if (availableQuote.length) {
      this.setState({
        gross: availableQuote[0].gross,
      });
      if (quote.biannualFirstAmount) {
        this.setState({
          gross: quote.biannualFirstAmount,
        });
      }
    }
  }

  /*****************************
   Encrypt fields before submit
   ****************************/

  private encryptBeforeSubmit = () => {
    const { dispatch, change, cardNumber, cvv, actions } = this.props;
    // const encryptedCardNumber = this.encrypt(cardNumber);
    // const encryptedCardCvv = this.encrypt(cvv);

    const encryptedCardNumber = cardNumber;
    const encryptedCardCvv = cvv;

    const promise = new Promise(() => {
      dispatch(change('card.cardNumber', encryptedCardNumber));
      dispatch(change('card.cvv', encryptedCardCvv));
      actions.submit('quote');
    });

    return promise;
  }

  private updateActiveField = (name) => () => {
    this.setState({
      activeField: name,
    });
  }

  private declinesOrReferrals() {
    const {declines, referrals, errors} = this.state;

    if (!declines.length && !referrals.length && !errors.length) {
      return null;
    }

    return (
      <div>
        <DeclinesAndReferrals
          declines={declines}
          referrals={referrals}
          errors={errors}
          quoteReference={this.props.quote.application.quoteReference}
          renewal={this.props.renewal}
        />
        {(! declines.length && ! referrals.length && errors.length) && (
          <NavigationBar
            hasBackButton={true}
            backTo={'/validation'}
            hasNextButton={false}
          />
        )}
      </div>
    );
  }

  public render() {
    const {
      cvv,
      quote,
      cardName,
      cardNumber,
      expiryYear,
      handleSubmit,
      errorMessage,
      expiryMonth,
    } = this.props;
    const { activeField } = this.state;
    const quotes = quote.quotes.standard && quote.quotes.standard;
    const availableQuote = [];

    /**********************
     Normalize: max-length
    **********************/
    const lessThan = (limit) => (value, previousValue, allValues) => {
      return value.length < limit ? value : previousValue;
    };

    map(quotes, (quote, key) => {
      availableQuote.push(quote);
    });

    const declinesOrReferrals = this.declinesOrReferrals();
    if (declinesOrReferrals) {
      return declinesOrReferrals;
    }

    const generalConditionsDocument = availableQuote[0].scheme.documents['Conditions Generales: ' + PRODUCT_NAME].path;

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
        <div className="steps-container">
          <div className="animated fadeIn">
            <h3>Paiement</h3>

            {this.state.gross > 0 && (
              <h4>Montant à payer: <span className="red premium">{displayCurrency(this.state.gross)} €</span></h4>
            )}

            {quote.application.paymentSchedule === 'bi-annual' && (
              <p>Vous avez choisi un paiement semestriel : la prime du second semestre sera débitée automatiquement le
                1er juillet prochain</p>
            )}

            <div className="margin-bottom">
              <Cards
                cvc={cvv || ''}
                focused={activeField}
                name={cardName || ''}
                number={cardNumber || ''}
                expiry={(expiryMonth || '') + (expiryYear || '')}
                locale={{valid: 'valable jusqu\'à'}}
                placeholders={{name: 'VOTRE NOM ICI'}}
              />
            </div>
            <FormGroup controlId="card-number" className="margin-top">
              <Col componentClass={ControlLabel} sm={3}>
                Numéro de carte:
              </Col>
              <Col sm={9}>
                <Field
                  type="cleave"
                  name="cardNumber"
                  component={FieldFormControl}
                  validate={[isRequired, isLuhnHappy()]}
                  onFocus={this.updateActiveField('number')}
                  cleaveOptions={{ creditCard: true, creditCardStrictMode: true }}
                />
              </Col>
            </FormGroup>
            <FormGroup controlId="expiry-dates">
              <Col componentClass={ControlLabel} sm={3}>
                Date d'expiration:
              </Col>
              <Col sm={4}>
                <Row>
                  <Col sm={6}>
                    <Field
                      placeholder="mm"
                      normalize={lessThan(3)}
                      validate={[isRequired]}
                      name={'card.expiryMonth'}
                      component={FieldFormControl}
                      onFocus={this.updateActiveField('expiry')}
                    />
                  </Col>
                  <Col sm={6}>
                    <Field
                      placeholder="yy"
                      normalize={lessThan(3)}
                      validate={[isRequired]}
                      name={'card.expiryYear'}
                      component={FieldFormControl}
                    />
                  </Col>
                </Row>
              </Col>
            </FormGroup>
            <FormGroup controlId="expiry-dates">
              <Col componentClass={ControlLabel} sm={3}>
                Nom du titulaire de la carte:
              </Col>
              <Col sm={9}>
                <Field
                  name="card.name"
                  validate={[isRequired]}
                  component={FieldFormControl}
                  onFocus={this.updateActiveField('name')}
                />
              </Col>
            </FormGroup>
            <FormGroup controlId="cvv">
              <Col componentClass={ControlLabel} sm={3}>
                CVV:
              </Col>
              <Col sm={2}>
                <Field
                  name="cvv"
                  normalize={lessThan(5)}
                  component={FieldFormControl}
                  onBlur={this.updateActiveField('')}
                  onFocus={this.updateActiveField('cvc')}
                  validate={[
                    isRequired,
                    isLengthBetween([3, 4], 'The CVC/CVV incorrect, please type it in again.'),
                  ]}
                />
              </Col>
            </FormGroup>
          </div>

          {errorMessage && (
            <div className="error-block-container">
              <FaExclamationTriangle/><p>{translatePaymentError(errorMessage)}</p>
            </div>
          )}
          <NavigationBar
            onClickSubmit={this.encryptBeforeSubmit}
            isInPaymentPage={true}
            hasBackButton={true}
            backTo={'/validation'}
            {...this.props}
            saveQuoteModal={
              <SaveQuoteModal
                quoteReference={this.props.quote.application.quoteReference}
                {...this.props}
                buttonText={'Je sauvegarde et reviendrai plus tard'}
                generalConditionsDocument={generalConditionsDocument}
              />
            }
          />
        </div>
      </Form>
    );
  }
}

const validate = (values) => {
  const errors = { passwordConfirmation: '' };
  if (values.passwordConfirmation !== values.password) {
    errors.passwordConfirmation = 'Les mots de passe ne sont pas identiques, merci de les entrer à nouveau.';
  }
  return errors;
};

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    validate,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const cvv = selector(state, 'cvv');
      const quotes = state.quote.quotes;
      const channel = state.branding.channel;
      const password = selector(state, 'password');
      const schemeId = selector(state, 'schemeId');
      const errorMessage = state.quote.errorMessage;
      const cardName = selector(state, 'card.name');
      const paymentInfo = selector(state, 'payment');
      const cardNumber = selector(state, 'cardNumber');
      const paymentType = selector(state, 'paymentType');
      const expiryYear = selector(state, 'card.expiryYear');
      const expiryMonth = selector(state, 'card.expiryMonth');
      const encryptCardNumber = selector(state, 'card.cardNumber');
      const policyDeclaration = selector(state, 'policyDeclaration');
      const passwordConfirmation = selector(state, 'passwordConfirmation');

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });

      return {
        cvv,
        premium,
        channel,
        schemeId,
        password,
        cardName,
        expiryYear,
        cardNumber,
        paymentType,
        expiryMonth,
        paymentInfo,
        errorMessage,
        policyDeclaration,
        encryptCardNumber,
        passwordConfirmation,
      };
    },
    (dispatch) => ({
      dispatch,
      actions: {
        submit: bindActionCreators(submit, dispatch),
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({...quoteActions}, dispatch),
      },
    }),
  ),
])(PaymentForm);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/Payment/index.tsx